// API 호출을 위한 유틸리티 함수들

/**
 * 서버 설정을 기반으로 API 요청을 보내는 유틸리티 클래스
 */
class ApiService {
  constructor(serverConfig) {
    this.baseUrl = serverConfig.baseUrl;
    this.timeout = serverConfig.apiTimeout;
    this.serverId = serverConfig.id;
  }

  /**
   * API 엔드포인트 URL 생성
   * @param {string} endpoint - API 엔드포인트
   * @returns {string} 프록시 경로를 포함한 전체 URL
   */
  getUrl(endpoint) {
    // 프록시 경로 생성 (stock-api, chart-api 등)
    const simplifiedId = this.serverId.replace('-server', '');
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    return `/${simplifiedId}${cleanEndpoint}`;
  }

  /**
   * HTTP 요청을 보내는 기본 메서드
   * @param {string} endpoint - API 엔드포인트
   * @param {Object} options - fetch 옵션
   * @returns {Promise<Object>} API 응답
   */
  async request(endpoint, options = {}) {
    const url = this.getUrl(endpoint);
    
    const defaultOptions = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      timeout: this.timeout,
    };

    const requestOptions = { ...defaultOptions, ...options };

    try {
      // 타임아웃 처리
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('요청 시간이 초과되었습니다')), this.timeout)
      );

      const response = await Promise.race([
        fetch(url, requestOptions),
        timeoutPromise
      ]);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return await response.json();
      } else {
        return await response.text();
      }
    } catch (error) {
      console.error(`API 요청 실패 (${url}):`, error);
      throw error;
    }
  }

  /**
   * GET 요청
   * @param {string} endpoint - API 엔드포인트
   * @param {Object} params - 쿼리 파라미터
   * @returns {Promise<Object>} API 응답
   */
  async get(endpoint, params = {}) {
    let url = endpoint;
    
    // 쿼리 파라미터 추가
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        queryParams.append(key, value);
      }
    });

    if (queryParams.toString()) {
      url += `?${queryParams.toString()}`;
    }

    return this.request(url, { method: 'GET' });
  }

  /**
   * POST 요청
   * @param {string} endpoint - API 엔드포인트
   * @param {Object} data - 요청 데이터
   * @returns {Promise<Object>} API 응답
   */
  async post(endpoint, data = {}) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * POST 요청 (Query Parameter 방식)
   * @param {string} endpoint - API 엔드포인트
   * @param {Object} params - 쿼리 파라미터
   * @returns {Promise<Object>} API 응답
   */
  async postWithQuery(endpoint, params = {}) {
    let url = endpoint;

    // 쿼리 파라미터 추가
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        queryParams.append(key, value);
      }
    });

    if (queryParams.toString()) {
      url += `?${queryParams.toString()}`;
    }

    return this.request(url, { method: 'POST' });
  }

  /**
   * PUT 요청
   * @param {string} endpoint - API 엔드포인트
   * @param {Object} data - 요청 데이터
   * @returns {Promise<Object>} API 응답
   */
  async put(endpoint, data = {}) {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  /**
   * DELETE 요청
   * @param {string} endpoint - API 엔드포인트
   * @returns {Promise<Object>} API 응답
   */
  async delete(endpoint) {
    return this.request(endpoint, { method: 'DELETE' });
  }

  /**
   * 서버 연결 상태 확인
   * @returns {Promise<boolean>} 연결 성공 여부
   */
  async healthCheck() {
    try {
      await this.get('/api/health');
      return true;
    } catch (error) {
      console.error('서버 연결 확인 실패:', error);
      return false;
    }
  }
}

/**
 * 다중 서버 환경에서 API 호출을 관리하는 클래스
 */
class MultiServerApiManager {
  constructor(serverConfigs) {
    this.servers = serverConfigs;
    this.apiServices = {};
    
    // 각 서버별 ApiService 인스턴스 생성
    this.servers.forEach(server => {
      this.apiServices[server.id] = new ApiService(server);
    });
  }

  /**
   * 특정 서버의 ApiService 인스턴스 반환
   * @param {string} serverId - 서버 ID
   * @returns {ApiService} API 서비스 인스턴스
   */
  getApiService(serverId) {
    const service = this.apiServices[serverId];
    if (!service) {
      throw new Error(`서버를 찾을 수 없습니다: ${serverId}`);
    }
    return service;
  }

  /**
   * 활성화된 서버 목록 반환
   * @returns {Array} 활성 서버 목록
   */
  getActiveServers() {
    return this.servers.filter(server => server.isActive);
  }

  /**
   * 서버 설정 업데이트
   * @param {Array} newServerConfigs - 새로운 서버 설정 배열
   */
  updateServers(newServerConfigs) {
    this.servers = newServerConfigs;
    this.apiServices = {};
    
    // ApiService 인스턴스 재생성
    this.servers.forEach(server => {
      this.apiServices[server.id] = new ApiService(server);
    });
  }
}

/**
 * 거래대금 관련 API 호출 함수들
 */
export const TradingApi = {
  /**
   * 실시간 거래대금 상위 데이터 조회
   * @param {string} serverId - 사용할 서버 ID
   * @param {MultiServerApiManager} apiManager - API 매니저 인스턴스
   * @param {Object} params - 조회 조건
   * @returns {Promise<Array>} 거래대금 데이터 배열
   */
  async getRealTimeData(serverId, apiManager, { date, time, timeUnit }) {
    try {
      const apiService = apiManager.getApiService(serverId);
      const response = await apiService.get('/api/trading/realtime', {
        date,
        time,
        timeUnit
      });
      return response.data || response;
    } catch (error) {
      console.error(`실시간 데이터 조회 실패 (서버: ${serverId}):`, error);
      throw error;
    }
  },

  /**
   * 눌림 거래대금 상위 데이터 조회
   * @param {string} serverId - 사용할 서버 ID
   * @param {MultiServerApiManager} apiManager - API 매니저 인스턴스
   * @param {Object} params - 조회 조건
   * @returns {Promise<Array>} 거래대금 데이터 배열
   */
  async getPressedData(serverId, apiManager, { date, time, timeUnit }) {
    try {
      const apiService = apiManager.getApiService(serverId);
      const response = await apiService.get('/api/trading/pressed', {
        date,
        time,
        timeUnit
      });
      return response.data || response;
    } catch (error) {
      console.error(`눌림 데이터 조회 실패 (서버: ${serverId}):`, error);
      throw error;
    }
  },

  /**
   * 테마별 거래대금 상위 데이터 조회
   * @param {string} serverId - 사용할 서버 ID
   * @param {MultiServerApiManager} apiManager - API 매니저 인스턴스
   * @param {Object} params - 조회 조건
   * @returns {Promise<Array>} 거래대금 데이터 배열
   */
  async getThemeData(serverId, apiManager, { theme, date, time }) {
    try {
      const apiService = apiManager.getApiService(serverId);
      const response = await apiService.get('/api/trading/theme', {
        theme,
        date,
        time
      });
      return response.data || response;
    } catch (error) {
      console.error(`테마 데이터 조회 실패 (서버: ${serverId}):`, error);
      throw error;
    }
  }
};

/**
 * MultiServerApiManager 인스턴스를 생성하는 팩토리 함수
 * @param {Array} serverConfigs - 서버 설정 배열
 * @returns {MultiServerApiManager} 다중 서버 API 매니저 인스턴스
 */
export const createMultiServerApiManager = (serverConfigs) => {
  return new MultiServerApiManager(serverConfigs);
};

/**
 * ApiService 인스턴스를 생성하는 팩토리 함수 (단일 서버용)
 * @param {Object} serverConfig - 서버 설정
 * @returns {ApiService} API 서비스 인스턴스
 */
export const createApiService = (serverConfig) => {
  return new ApiService(serverConfig);
};

export { MultiServerApiManager };
export default ApiService;